import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Clock, Phone } from "lucide-react";
import { getPostBySlug, type BlogPostListItem } from "@/lib/blog";
import { resolveBlogImage } from "@/lib/blog-images";
import { CategoryChip } from "@/components/CategoryChip";
import { BlogCard } from "@/components/BlogCard";
import { Button } from "@/components/ui/button";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { useI18n } from "@/lib/i18n";

const SITE = "https://www.amr3designconstruction.com";
const FALLBACK_OG = `${SITE}/og-blog.jpg`;

function metaDescription(s: string, max = 155) {
  if (!s || s.length <= max) return s;
  const cut = s.slice(0, max);
  const end = Math.max(cut.lastIndexOf(". "), cut.lastIndexOf("! "), cut.lastIndexOf("? "));
  if (end >= 100) return cut.slice(0, end + 1);
  return cut.slice(0, cut.lastIndexOf(" ")).replace(/[,;:—–-]+$/, "") + "…";
}

function truncate(s: string, n: number) {
  if (!s) return s;
  return s.length <= n ? s : s.slice(0, n - 1).trimEnd() + "…";
}

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }) => {
    const data = await getPostBySlug(params.slug);
    return data;
  },
  head: ({ params, loaderData }) => {
    const url = `${SITE}/blog/${params.slug}`;
    const post = loaderData?.post;
    const rawTitle = post?.title_en || params.slug.replace(/-/g, " ");
    const title = truncate(`${rawTitle} | AMR 3 Blog`, 60);
    const description = metaDescription(
      post?.excerpt_en ||
        "Roofing & solar insights for New Mexico homeowners — AMR 3 Blog.",
    );
    const cover = post?.cover_image_url;
    // Build an absolute OG image URL at exactly 1200x630 (1.91:1) for WhatsApp/Facebook/Twitter.
    // Sanity CDN supports query-string transforms (w, h, fit=crop).
    let img = FALLBACK_OG;
    if (cover) {
      if (/^https?:\/\//i.test(cover)) {
        img = cover.includes("cdn.sanity.io")
          ? `${cover.split("?")[0]}?w=1200&h=630&fit=crop&auto=format`
          : cover;
      } else {
        img = `${SITE}${cover.startsWith("/") ? cover : `/${cover}`}`;
      }
    }
    const scripts = post
      ? [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              headline: rawTitle,
              description,
              image: img,
              datePublished: post.published_at,
              dateModified: post.published_at,
              mainEntityOfPage: { "@type": "WebPage", "@id": url },
              author: { "@type": "Organization", name: "AMR 3 Design & Construction LLC" },
              publisher: {
                "@type": "Organization",
                name: "AMR 3 Design & Construction LLC",
                logo: { "@type": "ImageObject", url: `${SITE}/og-image-v3.jpg` },
              },
            }),
          },
        ]
      : undefined;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "og:image", content: img },
        { property: "og:image:secure_url", content: img },
        { property: "og:image:type", content: "image/jpeg" },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: rawTitle },
        ...(post?.published_at
          ? [{ property: "article:published_time" as const, content: post.published_at }]
          : []),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: img },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }],
      ...(scripts ? { scripts } : {}),
    };
  },
  component: BlogPostPage,
});

function BlogPostPage() {
  const { slug } = Route.useParams();
  const { lang, t } = useI18n();
  const { data, isLoading } = useQuery({
    queryKey: ["blog", "post", slug],
    queryFn: () => getPostBySlug(slug),
    initialData: Route.useLoaderData() ?? undefined,
    staleTime: 1000 * 60 * 5,
  });

  if (isLoading) {
    return (
      <main className="flex min-h-screen items-center justify-center px-4 pt-24 text-center">
        <p className="text-muted-foreground">{t("blog.loading")}</p>
      </main>
    );
  }

  if (!data?.post) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center px-4 pt-24 text-center">
        <h1 className="font-serif text-5xl font-bold text-primary">404</h1>
        <p className="mt-3 text-muted-foreground">Article not found.</p>
        <Link to="/blog" className="mt-6 text-accent-strong hover:underline">
          Back to blog
        </Link>
      </main>
    );
  }

  const { post, related } = data;
  const title = lang === "es" ? post.title_es : post.title_en;
  const content = lang === "es" ? post.content_es : post.content_en;
  const date = new Date(post.published_at).toLocaleDateString(lang === "es" ? "es-MX" : "en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <main className="pt-20">
      <section className="relative isolate h-[420px] w-full overflow-hidden sm:h-[520px]">
        <img
          src={resolveBlogImage(post.cover_image_url)}
          alt={title}
          width={1920}
          height={1080}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[oklch(0.14_0.03_50/0.82)] via-[oklch(0.14_0.03_50/0.48)] to-[oklch(0.14_0.03_50/0.14)]" />
        <div className="relative mx-auto flex h-full max-w-4xl flex-col justify-end px-4 pb-12 text-white sm:px-6">
          <Breadcrumbs
            items={[
              { label: t("nav.home"), to: "/" },
              { label: t("nav.blog"), to: "/blog" },
              { label: title },
            ]}
          />

          <CategoryChip
            category={post.category}
            className="self-start bg-background/90 backdrop-blur"
          />
          <h1 className="mt-4 font-serif text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl">
            {title}
          </h1>
          <div className="mt-4 flex items-center gap-3 text-sm text-white/90">
            <time dateTime={post.published_at}>{date}</time>
            <span aria-hidden>·</span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-4 w-4" />
              {post.read_minutes} {t("blog.minRead")}
            </span>
          </div>
        </div>
      </section>

      <article className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-20">
        <div className="prose-blog">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
        </div>

        <div className="mt-12 rounded-2xl border border-accent/30 bg-secondary/50 p-8 text-center shadow-sm">
          <h2 className="font-serif text-2xl font-bold text-primary">{t("blog.ctaTitle")}</h2>
          <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground">{t("blog.ctaSub")}</p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <Button asChild variant="gold" size="lg">
              <Link to="/contact" hash="project-form">{t("blog.ctaPrimary")}</Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <a href="tel:+15058680847" className="gap-2">
                <Phone className="h-4 w-4" /> (505) 868-0847
              </a>
            </Button>
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="bg-secondary/40 py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h2 className="mb-8 font-serif text-2xl font-bold text-primary sm:text-3xl">
              {t("blog.related")}
            </h2>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {(related as BlogPostListItem[]).map((p) => (
                <BlogCard key={p.id} post={p} />
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
